import { useMemo, useState } from "react";
import type { ContactAttempt, CreateContactAttemptDto, WorkOrderWithAppointments } from "@myapp/contract";
import {
  getAttemptsSummary,
  useAddContactAttempt,
  useDeleteContactAttempt,
  useUpdateContactComment,
  useUpdateWorkOrderStatus,
} from "../../../entities/work-order";
import { useDeleteDialog } from "../../../shared/hooks/useDeleteDialog";
import { getErrorMessage } from "../../../shared/lib/errors/getErrorMessage";
import { formatTimeDE, formatWeekdayDateDE } from "../../../shared/helpers/date";

type Params = {
  workOrderId: string;
  workOrder: WorkOrderWithAppointments;
  /** The office may change attempts and the comment; everyone else reads. */
  canEdit: boolean;
};

/**
 * Everything the „Kontaktversuche" card does, so the card itself only draws.
 *
 * Add, delete and comment are three mutations that all answer with the whole work order, which is
 * written into the detail cache — the counter moves with the click. Errors are kept per action and
 * shown beside the control that caused them; a failed add leaves the form open for a retry.
 * „Gelöscht" reuses the status mutation behind the same confirm dialog the title menu uses.
 */
export function useContactAttempts({ workOrderId, workOrder, canEdit }: Params) {
  const addMutation = useAddContactAttempt();
  const deleteMutation = useDeleteContactAttempt();
  const commentMutation = useUpdateContactComment();
  const statusMutation = useUpdateWorkOrderStatus();
  const deleteDialog = useDeleteDialog();
  const [cancelOpen, setCancelOpen] = useState(false);

  // `?? []`: the web is released on its own, so for a moment it can meet an API that has not
  // learned these fields yet. The card then shows an empty list instead of taking the page down.
  const attempts = workOrder.contactAttempts ?? [];
  const summary = useMemo(
    () =>
      getAttemptsSummary({
        count: attempts.length,
        notReachedSince: workOrder.notReachedSince ?? null,
        now: new Date(),
      }),
    [attempts.length, workOrder.notReachedSince],
  );

  const isNotReached = workOrder.status === "NOT_REACHED";
  // Adding needs the status, not only the role: the API refuses attempts on any other status.
  const canAdd = canEdit && isNotReached && !summary.isFull;

  const add = async (dto: CreateContactAttemptDto): Promise<boolean> => {
    addMutation.reset();
    try {
      await addMutation.mutateAsync({ workOrderId, dto });
      return true;
    } catch {
      return false;
    }
  };

  const askRemove = (attempt: ContactAttempt) => {
    const position = attempts.findIndex((a) => a.id === attempt.id) + 1;
    deleteDialog.openDelete(
      `${position}. Kontaktversuch löschen?`,
      async () => {
        await deleteMutation.mutateAsync({ workOrderId, attemptId: attempt.id });
      },
      {
        description: `${formatWeekdayDateDE(attempt.attemptedAt)}, ${formatTimeDE(attempt.attemptedAt)}${
          attempt.note ? ` · ${attempt.note}` : ""
        }`,
      },
    );
  };

  const saveComment = async (text: string): Promise<boolean> => {
    commentMutation.reset();
    try {
      await commentMutation.mutateAsync({ workOrderId, dto: { comment: text } });
      return true;
    } catch {
      return false;
    }
  };

  const confirmCancel = async () => {
    if (statusMutation.isPending) return;
    try {
      await statusMutation.mutateAsync({ workOrderId, dto: { status: "CANCELLED" } });
      setCancelOpen(false);
    } catch {
      // Stays open: the error is shown in the dialog, next to the button that retries.
    }
  };

  return {
    status: workOrder.status,
    attempts,
    summary,
    comment: workOrder.contactComment ?? "",
    canEdit,
    canAdd,
    canCancelOrder: canEdit && isNotReached && summary.isFull,

    add,
    isAdding: addMutation.isPending,
    addError: addMutation.isError ? getErrorMessage(addMutation.error) : null,

    askRemove,
    deleteDialog,

    saveComment,
    isSavingComment: commentMutation.isPending,
    commentError: commentMutation.isError ? getErrorMessage(commentMutation.error) : null,

    cancelPrompt: {
      isOpen: cancelOpen,
      isPending: statusMutation.isPending,
      error: statusMutation.isError ? getErrorMessage(statusMutation.error) : null,
      open: () => {
        statusMutation.reset();
        setCancelOpen(true);
      },
      close: () => {
        if (statusMutation.isPending) return;
        setCancelOpen(false);
        statusMutation.reset();
      },
      confirm: confirmCancel,
    },
  };
}

export type ContactAttemptsController = ReturnType<typeof useContactAttempts>;
